import PropTypes from 'prop-types';

import { useContext, Fragment } from 'react';

import MaterialsCaptureContext from '../../context/MaterialsCaptureContext';

import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import FormControl from '@mui/material/FormControl';
import Autocomplete from '@mui/material/Autocomplete';
import Typography from '@mui/material/Typography';
import ButtonGroup from '@mui/material/ButtonGroup';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

import MaterialDivider from './MaterialDivider';
import MaterialSubDivider from './MaterialSubDivider';

const LipidForm = ({ data, index }) => {
  const { 
    handleDelete, handleInputChange, initialValues, formErrors, 
    handleAliquotChange, handleAliquotInputChange,
  } = useContext(MaterialsCaptureContext);
  
  return(
    <Box mt={1}>
      <MaterialDivider 
        index={index} 
        title={'Lipid'} 
        showDeleteIcon={data?.is_new_lipid} 
        handleDelete={handleDelete} 
        deleteIndex={data?.index} 
      />
      
      <Grid container spacing={1} mt={1}>
        <Grid item xs={6}>
          <Autocomplete
            value={data?.lipid_type}
            onChange={(e, newValue) => 
              handleInputChange(e, data?.index, 'lipid_type', newValue)
            }
            onInputChange={(e, newValue) =>
              handleInputChange(e, data?.index, 'lipid_type', newValue)
            }
            options={Object.keys(initialValues?.lipids)}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label="Lipid Type" 
                placeholder="Type to search Lipid type..." 
                error={`lipid_type-${data?.index}` in formErrors}
                helperText={formErrors[`lipid_type-${data?.index}`]}
              />
            )}
            disabled={!data?.is_new_lipid}
            freeSolo
            fullWidth
          />
        </Grid>

        <Grid item xs={6}>
          <Autocomplete
            value={data?.lipid_name}
            onChange={(e, newValue) => 
              handleInputChange(e, data?.index, 'lipid_name', newValue)
            }
            onInputChange={(e, newValue) =>
              handleInputChange(e, data?.index, 'lipid_name', newValue)
            }
            options={
              data?.lipid_type !== null ? initialValues?.lipids[data?.lipid_type] : []
            }
            renderInput={(params) => (
              <TextField 
                {...params} 
                label="Lipid Name" 
                placeholder="Type to search Lipid name..." 
                error={`lipid_name-${data?.index}` in formErrors}
                helperText={formErrors[`lipid_name-${data?.index}`]}
              />
            )}
            disabled={!data?.is_new_lipid}
            freeSolo
            fullWidth
          />
        </Grid>

        <Grid item xs={6}>
          <Autocomplete
            value={data?.supplier}
            onChange={(e, newValue) => 
              handleInputChange(e, data?.index, 'supplier', newValue)
            }
            onInputChange={(e, newValue) =>
              handleInputChange(e, data?.index, 'supplier', newValue)
            }
            options={initialValues?.supplierArray}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label="Supplier" 
                placeholder="Type to search Supplier..." 
                error={`supplier-${data?.index}` in formErrors}
                helperText={formErrors[`supplier-${data?.index}`]}
              />
            )}
            freeSolo
            fullWidth
          />
        </Grid>

        <Grid item xs={6}>
          <Autocomplete
            value={data?.product_code}
            onChange={(e, newValue) => 
              handleInputChange(e, data?.index, 'product_code', newValue)
            }
            onInputChange={(e, newValue) =>
              handleInputChange(e, data?.index, 'product_code', newValue)
            }
            options={initialValues?.productCodeArray}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label="Product code" 
                placeholder="Type to search Product code..." 
                error={`product_code-${data?.index}` in formErrors}
                helperText={formErrors[`product_code-${data?.index}`]}
              />
            )}
            freeSolo
            fullWidth
          />
        </Grid>

        <Grid item xs={6}>
          <Autocomplete
            value={data?.lot_number}
            onChange={(e, newValue) => 
              handleInputChange(e, data?.index, 'lot_number', newValue)
            }
            onInputChange={(e, newValue) =>
              handleInputChange(e, data?.index, 'lot_number', newValue)
            }
            options={initialValues?.lotNumberArray}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label="Lot number" 
                placeholder="Type to search Lot number..." 
                error={`lot_number-${data?.index}` in formErrors}
                helperText={formErrors[`lot_number-${data?.index}`]}
              />
            )}
            freeSolo
            fullWidth
          />
        </Grid>

        <Grid item xs={6}>
          <TextField
            varient="outlined"
            label="Number of ionisable groups"
            name={`number_of_ionisable_groups-${data?.index}`}
            value={data?.number_of_ionisable_groups}
            onChange={e => 
              handleInputChange(
                e, data?.index, 'number_of_ionisable_groups', e.target.value
              )
            }
            type='number'
            helperText={formErrors[`number_of_ionisable_groups-${data?.index}`]}
            error={`number_of_ionisable_groups-${data?.index}` in formErrors}
            fullWidth
          />
        </Grid>

        <Grid item xs={12} sx={{display: 'flex', mt: 1}}>
          <Typography
            color='text.secondary'
            variant='h6'
          >
            <strong>{`Number of Aliquots: ${data?.number_of_aliquots}`}</strong>
          </Typography>

          <ButtonGroup sx={{ ml: 3}}>
            <Button
              aria-label="reduce"
              onClick={(e) => {
                handleAliquotChange(e, data?.index, 'sub');
              }}
            >
              <RemoveIcon fontSize="small" />
            </Button>
            <Button
              aria-label="increase"
               onClick={(e) => {
                handleAliquotChange(e, data?.index, 'add');
              }}
            >
              <AddIcon fontSize="small" />
            </Button>
          </ButtonGroup>
        </Grid>

        {
          data?.aliquot_list.map((row, index) => (
            <Fragment key={index}>
              <MaterialSubDivider title={`Lipid Aliquot ${index + 1}`} />
              <Grid item xs={6}>
                <TextField
                  varient="outlined"
                  label="Unique aliquot ID"
                  name={`aliquot_id-${row?.index}`}
                  value={row?.aliquot_id}
                  onChange={e => 
                    handleAliquotInputChange(e, data?.index, row?.index, 'aliquot_id', e.target.value)
                  }
                  helperText={formErrors[`aliquot_id-${row?.index}`]}
                  error={`aliquot_id-${row?.index}` in formErrors}
                  fullWidth
                />
              </Grid>

              <Grid item xs={6}>
                <FormControl fullWidth>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label='Date aliquoted'
                      name={`date_aliquoted-${row?.index}`}
                      value={row?.date_aliquoted} 
                      onChange={e => 
                        handleAliquotInputChange(e, data?.index, row?.index, 'date_aliquoted', e)
                      } 
                      maxDate={initialValues?.today}
                      renderInput={(params) => 
                        <TextField 
                          {...params} 
                          error={Boolean(formErrors[`date_aliquoted-${row?.index}`])}
                          helperText={formErrors[`date_aliquoted-${row?.index}`]}
                        />
                      }
                    />
                  </LocalizationProvider>
                </FormControl>
              </Grid>

              <Grid item xs={6}>
                <Autocomplete
                  value={row?.aliquot_operator}
                  onChange={(e, newValue) => 
                    handleAliquotInputChange(e, data?.index, row?.index, 'aliquot_operator', newValue)
                  }
                  onInputChange={(e, newValue) =>
                    handleAliquotInputChange(e, data?.index, row?.index, 'aliquot_operator', newValue)
                  }
                  options={initialValues?.operatorArray}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      label="Operator" 
                      placeholder="Type to search Operator..." 
                      error={`aliquot_operator-${row?.index}` in formErrors}
                      helperText={formErrors[`aliquot_operator-${row?.index}`]}
                    />
                  )}
                  freeSolo
                  fullWidth
                />
              </Grid>
            </Fragment>
          ))
        }

        <MaterialSubDivider title={'Lipid Stock'} />

        <Grid item xs={6}>
          <TextField
            varient="outlined"
            label="Unique lipid stock ID"
            name={`lipd_stock_id-${data?.index}`}
            value={data?.lipd_stock_id}
            onChange={e => 
              handleInputChange(e, data?.index, 'lipd_stock_id', e.target.value)
            }
            helperText={formErrors[`lipd_stock_id-${data?.index}`]}
            error={`lipd_stock_id-${data?.index}` in formErrors}
            fullWidth
          />
        </Grid>

        <Grid item xs={6}>
          <Autocomplete
            value={data?.solvent}
            onChange={(e, newValue) => 
              handleInputChange(e, data?.index, 'solvent', newValue)
            }
            onInputChange={(e, newValue) =>
              handleInputChange(e, data?.index, 'solvent', newValue)
            }
            options={initialValues?.solventArray}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label="Solvent" 
                placeholder="Type to search Solvent..." 
                error={`solvent-${data?.index}` in formErrors}
                helperText={formErrors[`solvent-${data?.index}`]}
              />
            )}
            freeSolo
            fullWidth
          />
        </Grid>

        <Grid item xs={6}>
          <TextField
            varient="outlined"
            label="Concentration (mg/mL)"
            name={`concentration-${data?.index}`}
            value={data?.concentration}
            onChange={e => 
              handleInputChange(e, data?.index, 'concentration', e.target.value)
            }
            type='number'
            helperText={formErrors[`concentration-${data?.index}`]}
            error={`concentration-${data?.index}` in formErrors}
            fullWidth
          />
        </Grid>

        <Grid item xs={6}>
          <FormControl fullWidth>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label='Date prepared'
                name={`date_prepared-${data?.index}`}
                value={data?.date_prepared} 
                onChange={e => 
                  handleInputChange(e, data?.index, 'date_prepared', e)
                } 
                maxDate={initialValues?.today}
                renderInput={(params) => 
                  <TextField 
                    {...params} 
                    error={Boolean(formErrors[`date_prepared-${data?.index}`])}
                    helperText={formErrors[`date_prepared-${data?.index}`]}
                  />
                }
              />
            </LocalizationProvider>
          </FormControl>
        </Grid>

        <Grid item xs={6}>
          <Autocomplete
            value={data?.stock_operator}
            onChange={(e, newValue) => 
              handleInputChange(e, data?.index, 'stock_operator', newValue)
            }
            onInputChange={(e, newValue) =>
              handleInputChange(e, data?.index, 'stock_operator', newValue)
            }
            options={initialValues?.operatorArray}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label="Operator" 
                placeholder="Type to search Operator..." 
                error={`stock_operator-${data?.index}` in formErrors}
                helperText={formErrors[`stock_operator-${data?.index}`]}
              />
            )}
            freeSolo
            fullWidth
          />
        </Grid>

        <Grid item xs={6}>
          <TextField
            varient="outlined"
            label="Amount prepared (mL)"
            name={`amount_prepared-${data?.index}`}
            value={data?.amount_prepared}
            onChange={e => 
              handleInputChange(e, data?.index, 'amount_prepared', e.target.value)
            }
            type='number'
            helperText={formErrors[`amount_prepared-${data?.index}`]}
            error={`amount_prepared-${data?.index}` in formErrors}
            fullWidth
          />
        </Grid>
      </Grid>
    </Box>
  );
};

LipidForm.propTypes = {
  data: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
};

export default LipidForm;